/**
 * EditIdentityDialog — legal name + GSTIN.
 *
 * Both fields land in the same patch. GSTIN is the upgrade path
 * from a B2C-style customer (no GSTIN) to a B2B one. We don't
 * re-run the GSTN lookup from here (that's the new-customer
 * flow); the server's regex on `gstin` is the source of truth
 * for validity, but we mirror it client-side so the user gets
 * inline feedback instead of a round-trip failure.
 */
import * as React from "react";
import { Input } from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { EditDialog } from "./edit-dialog.tsx";
import { SectionField } from "./section-field.tsx";

// Same regex the server enforces in `packages/api-contract` — a 15-char
// GSTIN: 2-digit state, 10-char PAN, 1 entity number, 1 default 'Z',
// 1 check char. Mirrored client-side to avoid a round-trip on obviously
// wrong input. Kept in sync with the contract via the AC29 fix.
const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

type CustomerRow = z.infer<typeof contract.customerRow>;

export interface EditIdentityDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  customer: CustomerRow;
  pending: boolean;
  serverError: string | null;
  onSave: (patch: { name?: string; gstin?: string }) => Promise<void>;
}

export function EditIdentityDialog({
  open,
  onOpenChange,
  customer,
  pending,
  serverError,
  onSave,
}: EditIdentityDialogProps) {
  const [name, setName] = React.useState(customer.name);
  const [gstin, setGstin] = React.useState(customer.gstin ?? "");
  const [gstinError, setGstinError] = React.useState<string | null>(null);
  const [nameError, setNameError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setName(customer.name);
      setGstin(customer.gstin ?? "");
      setGstinError(null);
      setNameError(null);
    }
  }, [open, customer.name, customer.gstin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedGstin = gstin.trim().toUpperCase();

    // Client-side pre-flight — mirrors the server contract so obviously
    // bad input surfaces inline instead of coming back as a Zod round-trip.
    let hasError = false;
    if (!trimmedName) {
      setNameError("Legal name is required.");
      hasError = true;
    } else {
      setNameError(null);
    }
    if (trimmedGstin !== "" && !GSTIN_PATTERN.test(trimmedGstin)) {
      setGstinError("Enter a valid 15-character GSTIN (e.g. 29AABCC1234A1Z5).");
      hasError = true;
    } else {
      setGstinError(null);
    }
    if (hasError) return;

    const patch: { name?: string; gstin?: string } = {};
    if (trimmedName && trimmedName !== customer.name) patch.name = trimmedName;
    if (trimmedGstin !== (customer.gstin ?? "")) patch.gstin = trimmedGstin;
    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    await onSave(patch);
  };

  return (
    <EditDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit identity & registration"
      description="Legal name appears on tax invoices and the e-invoice IRN payload. GSTIN is required for B2B billing — adding one upgrades this customer from B2C to B2B."
      pending={pending}
      serverError={serverError}
      onSubmit={handleSubmit}
    >
      <SectionField label="Legal name" required error={nameError}>
        <Input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          placeholder="e.g. Acme Trading Pvt Ltd"
          autoFocus
        />
      </SectionField>
      <SectionField
        label="GSTIN"
        optional="leave blank for B2C / unregistered"
        error={gstinError}
        hint={
          customer.gstin
            ? "Already on file. Editing replaces the value — make sure the new GSTIN is accurate."
            : "15-character GSTIN, e.g. 29AABCC1234A1Z5."
        }
      >
        <Input
          type="text"
          value={gstin}
          onChange={(e) => {
            setGstin(e.target.value.toUpperCase());
            if (gstinError) setGstinError(null);
          }}
          placeholder="27AABCS1234N1Z5"
          maxLength={15}
          spellCheck={false}
          autoComplete="off"
          className="font-mono tabular-nums uppercase tracking-wider"
        />
      </SectionField>
    </EditDialog>
  );
}
